import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { inr, fmtDate, statusColor } from "@/lib/utils-format";
import { Plus, Trash2, Eye, PackageCheck, Loader2 } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

const emptyItem = { ingredient_id: "", name: "", quantity: 1, unit: "kg", unit_price: 0, gst_rate: 5 };
const emptyForm = {
  supplier: { name: "", gstin: "", email: "", phone: "", address: "" },
  items: [{ ...emptyItem }],
  is_interstate: false,
  notes: "",
  expected_date: "",
};

export default function PurchaseOrdersPage() {
  const { user } = useAuth();
  const isSuperAdmin = (user?.role_id || user?.role) === "super_admin";
  const [pos, setPos] = useState([]);
  const [ingredients, setIngredients] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [viewing, setViewing] = useState(null);
  const [receiving, setReceiving] = useState(false);

  const load = async () => {
    const [p, i] = await Promise.all([api.get("/purchase-orders"), api.get("/ingredients")]);
    setPos(p.data); setIngredients(i.data);
  };
  useEffect(() => { load(); }, []);

  const updateItem = (i, k, v) => {
    setForm((f) => {
      const items = [...f.items];
      items[i] = { ...items[i], [k]: (k === "name" || k === "unit" || k === "ingredient_id") ? v : Number(v) || 0 };
      // Auto-fill name from ingredient
      if (k === "ingredient_id") {
        const ing = ingredients.find((x) => x.id === v);
        if (ing) {
          items[i].name = ing.name;
          items[i].unit = ing.unit;
          items[i].unit_price = ing.cost_per_unit;
        }
      }
      return { ...f, items };
    });
  };

  const totals = React.useMemo(() => {
    let sub = 0, gst = 0;
    form.items.forEach((it) => {
      const net = Number(it.quantity) * Number(it.unit_price);
      sub += net;
      gst += net * (Number(it.gst_rate || 0) / 100);
    });
    return { sub, gst, grand: sub + gst };
  }, [form.items]);

  const submit = async () => {
    if (!form.supplier.name) return toast.error("Supplier name required");
    if (!form.items[0].name) return toast.error("Add at least one item");
    try {
      await api.post("/purchase-orders", form);
      toast.success("Purchase order created");
      setOpen(false); setForm(emptyForm); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const view = async (po) => {
    const { data } = await api.get(`/purchase-orders/${po.id}`);
    setViewing(data);
  };

  const receive = async () => {
    setReceiving(true);
    try {
      await api.post(`/purchase-orders/${viewing.id}/receive`);
      toast.success("Stock received & ingredient stock updated");
      const { data } = await api.get(`/purchase-orders/${viewing.id}`);
      setViewing(data);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setReceiving(false); }
  };

  const remove = async (po) => {
    if (!window.confirm(`Delete ${po.number}?${po.status === "received" ? " Received raw-material stock and the purchase ledger entry will be reversed." : ""}`)) return;
    try {
      await api.delete(`/purchase-orders/${po.id}`);
      toast.success("Deleted");
      setViewing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-6" data-testid="pos-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="uc-label">Procurement</div>
          <h1 className="font-[Outfit] text-3xl sm:text-4xl font-semibold tracking-tight mt-1">Purchase Orders</h1>
        </div>
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setForm(emptyForm); }}>
          <DialogTrigger asChild>
            <Button data-testid="po-add-btn" className="bg-[#C05621] hover:bg-[#9C4221] text-white">
              <Plus className="w-4 h-4 mr-1.5" /> New PO
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl bg-white max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-[Outfit]">New Purchase Order</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              <div>
                <div className="uc-label mb-1">Supplier</div>
                <div className="grid grid-cols-2 gap-3">
                  <Input placeholder="Supplier name *" value={form.supplier.name} onChange={(e) => setForm({ ...form, supplier: { ...form.supplier, name: e.target.value } })} data-testid="po-supplier-name" />
                  <Input placeholder="GSTIN" value={form.supplier.gstin} onChange={(e) => setForm({ ...form, supplier: { ...form.supplier, gstin: e.target.value } })} />
                  <Input placeholder="Phone" value={form.supplier.phone} onChange={(e) => setForm({ ...form, supplier: { ...form.supplier, phone: e.target.value } })} />
                  <Input placeholder="Email" value={form.supplier.email} onChange={(e) => setForm({ ...form, supplier: { ...form.supplier, email: e.target.value } })} />
                  <Input placeholder="Address" className="col-span-2" value={form.supplier.address} onChange={(e) => setForm({ ...form, supplier: { ...form.supplier, address: e.target.value } })} />
                </div>
              </div>

              <div className="flex items-center gap-3">
                <Switch checked={form.is_interstate} onCheckedChange={(v) => setForm({ ...form, is_interstate: v })} />
                <Label>Inter-state (IGST)</Label>
                <div className="ml-auto flex items-center gap-2">
                  <Label>Expected date</Label>
                  <Input type="date" value={form.expected_date} onChange={(e) => setForm({ ...form, expected_date: e.target.value })} className="w-40 h-8" />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <div className="uc-label">Line Items</div>
                  <Button size="sm" variant="outline" onClick={() => setForm({ ...form, items: [...form.items, { ...emptyItem }] })}><Plus className="w-3 h-3 mr-1" /> Add</Button>
                </div>
                <div className="border border-[#EAE5D9] rounded-md overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-[#F7F2E8] uc-label">
                      <tr>
                        <th className="text-left px-2 py-2">Ingredient</th>
                        <th className="text-right px-2 py-2 w-20">Qty</th>
                        <th className="text-left px-2 py-2 w-20">Unit</th>
                        <th className="text-right px-2 py-2 w-24">Price</th>
                        <th className="text-right px-2 py-2 w-20">GST</th>
                        <th className="text-right px-2 py-2 w-28">Total</th>
                        <th className="w-8"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {form.items.map((it, i) => {
                        const net = Number(it.quantity) * Number(it.unit_price);
                        const gst = net * (it.gst_rate / 100);
                        return (
                          <tr key={i} className="border-t border-[#EAE5D9]">
                            <td className="px-2 py-1.5">
                              <Select value={it.ingredient_id || ""} onValueChange={(v) => updateItem(i, "ingredient_id", v)}>
                                <SelectTrigger className="h-8" data-testid={`po-ing-${i}`}><SelectValue placeholder="Select ingredient" /></SelectTrigger>
                                <SelectContent className="bg-white max-h-64">
                                  {ingredients.map((ing) => <SelectItem key={ing.id} value={ing.id}>{ing.name}</SelectItem>)}
                                </SelectContent>
                              </Select>
                            </td>
                            <td className="px-2 py-1.5"><Input type="number" value={it.quantity} onChange={(e) => updateItem(i, "quantity", e.target.value)} className="h-8 text-right" /></td>
                            <td className="px-2 py-1.5"><Input value={it.unit} onChange={(e) => updateItem(i, "unit", e.target.value)} className="h-8" /></td>
                            <td className="px-2 py-1.5"><Input type="number" value={it.unit_price} onChange={(e) => updateItem(i, "unit_price", e.target.value)} className="h-8 text-right" /></td>
                            <td className="px-2 py-1.5"><Input type="number" value={it.gst_rate} onChange={(e) => updateItem(i, "gst_rate", e.target.value)} className="h-8 text-right" /></td>
                            <td className="px-2 py-1.5 text-right font-medium">{inr(net + gst)}</td>
                            <td className="px-2 py-1.5">
                              {form.items.length > 1 && <button onClick={() => setForm({ ...form, items: form.items.filter((_, x) => x !== i) })} className="text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
                <div className="bg-[#F7F2E8] rounded-md p-4 space-y-1.5 text-sm">
                  <div className="flex justify-between"><span>Subtotal</span><span>{inr(totals.sub)}</span></div>
                  <div className="flex justify-between"><span>GST ({form.is_interstate ? "IGST" : "CGST+SGST"})</span><span>{inr(totals.gst)}</span></div>
                  <div className="flex justify-between pt-2 border-t border-[#EAE5D9] font-semibold"><span>Grand Total</span><span className="text-[#C05621]">{inr(totals.grand)}</span></div>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={submit} data-testid="po-save-btn" className="bg-[#C05621] hover:bg-[#9C4221] text-white">Create PO</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white border border-[#EAE5D9] rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left uc-label border-b border-[#EAE5D9]">
              <th className="px-4 py-3">Number</th>
              <th className="px-4 py-3">Supplier</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3 text-right">Total</th>
              <th className="px-4 py-3">Date</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {pos.map((po) => (
              <tr key={po.id} className="border-b border-[#EAE5D9] last:border-0 hover:bg-[#FDFBF7]" data-testid={`po-row-${po.id}`}>
                <td className="px-4 py-3 font-medium">{po.number}</td>
                <td className="px-4 py-3">{po.supplier?.name}</td>
                <td className="px-4 py-3"><Badge variant="outline" className={`capitalize ${statusColor(po.status)}`}>{po.status?.replace("_", " ")}</Badge></td>
                <td className="px-4 py-3 text-right font-semibold">{inr(po.grand_total)}</td>
                <td className="px-4 py-3 text-[#8B7355]">{fmtDate(po.created_at)}</td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => view(po)} className="p-1.5 hover:bg-[#F7F2E8] rounded" data-testid={`po-view-${po.id}`}><Eye className="w-4 h-4" /></button>
                  {isSuperAdmin && <button onClick={() => remove(po)} data-testid={`po-delete-${po.id}`} title="Delete (Super Admin — received stock & ledger auto-reversed)" className="p-1.5 hover:bg-red-50 text-red-600 rounded"><Trash2 className="w-4 h-4" /></button>}
                </td>
              </tr>
            ))}
            {pos.length === 0 && <tr><td colSpan={6} className="text-center py-10 text-[#8B7355]">No purchase orders yet.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent className="max-w-3xl bg-white max-h-[90vh] overflow-y-auto">
          {viewing && (
            <>
              <DialogHeader>
                <div className="flex items-center justify-between gap-3">
                  <DialogTitle className="font-[Outfit] text-2xl">{viewing.number}</DialogTitle>
                  <Badge variant="outline" className={`capitalize ${statusColor(viewing.status)}`}>{viewing.status?.replace("_", " ")}</Badge>
                </div>
              </DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <div className="uc-label mb-1">Supplier</div>
                    <div className="font-semibold">{viewing.supplier?.name}</div>
                    <div className="text-[#5C4033]">{viewing.supplier?.email}</div>
                    <div className="text-[#5C4033]">{viewing.supplier?.phone}</div>
                    {viewing.supplier?.gstin && <div className="text-[#5C4033]">GSTIN: {viewing.supplier.gstin}</div>}
                  </div>
                  <div className="text-right">
                    <div>Date: {fmtDate(viewing.created_at)}</div>
                    {viewing.expected_date && <div>Expected: {fmtDate(viewing.expected_date)}</div>}
                  </div>
                </div>
                <div className="border border-[#EAE5D9] rounded-md overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-[#F7F2E8] uc-label">
                      <tr><th className="text-left px-3 py-2">Item</th><th className="text-right px-3 py-2">Qty</th><th className="text-right px-3 py-2">Price</th><th className="text-right px-3 py-2">GST</th><th className="text-right px-3 py-2">Total</th></tr>
                    </thead>
                    <tbody>
                      {viewing.items?.map((it, i) => {
                        const net = it.quantity * it.unit_price;
                        const gst = net * (it.gst_rate / 100);
                        return (
                          <tr key={i} className="border-t border-[#EAE5D9]">
                            <td className="px-3 py-2">{it.name}</td>
                            <td className="px-3 py-2 text-right">{it.quantity} {it.unit}</td>
                            <td className="px-3 py-2 text-right">{inr(it.unit_price)}</td>
                            <td className="px-3 py-2 text-right">{it.gst_rate}%</td>
                            <td className="px-3 py-2 text-right font-medium">{inr(net + gst)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <div className="flex justify-end">
                  <div className="w-64 space-y-1 text-sm">
                    <div className="flex justify-between"><span>Subtotal</span><span>{inr(viewing.subtotal)}</span></div>
                    <div className="flex justify-between"><span>GST</span><span>{inr(viewing.total_gst)}</span></div>
                    <div className="flex justify-between border-t border-[#EAE5D9] pt-1.5 font-semibold text-lg text-[#C05621]"><span>Grand Total</span><span>{inr(viewing.grand_total)}</span></div>
                  </div>
                </div>
                {viewing.status !== "received" && (
                  <Button onClick={receive} disabled={receiving} data-testid="po-receive-btn" className="bg-emerald-600 hover:bg-emerald-700 text-white">
                    {receiving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <PackageCheck className="w-4 h-4 mr-2" />}
                    Mark Received (auto-add to stock)
                  </Button>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
